import type { Filter } from '#layers/data-model/types/filters';
import type { CompositePrimaryKey } from '#layers/data-model/types/schema/primary-key';
import { CURRENT_USER_VARIABLE } from '#layers/data-model/types/filters';

const CURRENT_USER_FIELD_PREFIX = `${CURRENT_USER_VARIABLE}.`;

export function toCurrentUserFieldVariable(fieldApiNames: string[]): string {
	return `${CURRENT_USER_VARIABLE}.${fieldApiNames.join('.')}`;
}

/**
 * Matches both forms the engine substitutes in permission rules: the bare `$CURRENT_USER` (which
 * resolves to the current user's primary key) and the nested `$CURRENT_USER.<field>` form.
 */
export function isCurrentUserVariable(value: unknown): boolean {
	return value === CURRENT_USER_VARIABLE || fromCurrentUserFieldVariable(value) !== null;
}

export function fromCurrentUserFieldVariable(value: unknown): string | null {
	if (typeof value !== 'string' || !value.startsWith(CURRENT_USER_FIELD_PREFIX)) return null;

	const fieldApiName = value.slice(CURRENT_USER_FIELD_PREFIX.length);
	return fieldApiName.length > 0 ? fieldApiName : null;
}

/**
 * Whether a key stands for the current user rather than a concrete record.
 *
 * No primary-key knowledge is needed: `toCompositePrimaryKey` refuses a primitive key for a
 * collection with more than one primary-key field, so the sentinel can only ever reach a key as the
 * sole value of a single-column one. Scanning every value is therefore sufficient — a composite key
 * carrying the sentinel in one column is beyond what any picker produces, and counts as a match here.
 */
export function isCurrentUserKey(key: CompositePrimaryKey): boolean {
	return Object.values(key).some((value) => isCurrentUserVariable(value));
}

/** Containers the filter tree recurses through. Anything else is a leaf operand. */
function isFilterBranch(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype;
}

/** Every leaf operand in a filter tree, in document order. */
function* filterOperands(value: unknown): Generator<unknown> {
	if (Array.isArray(value)) {
		for (const entry of value) yield* filterOperands(entry);
	}
	else if (isFilterBranch(value)) {
		for (const entry of Object.values(value)) yield* filterOperands(entry);
	}
	else {
		yield value;
	}
}

/** Rebuilds a filter tree with every leaf operand passed through `mapOperand`. */
function mapFilterOperands(value: unknown, mapOperand: (operand: unknown) => unknown): unknown {
	if (Array.isArray(value)) return value.map((entry) => mapFilterOperands(entry, mapOperand));

	if (isFilterBranch(value)) {
		return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, mapFilterOperands(entry, mapOperand)]));
	}

	return mapOperand(value);
}

/** Whether a filter uses the bare `variable` sentinel as an operand anywhere in its tree. */
export function filterContainsVariable(filter: Filter | null | undefined, variable: string): boolean {
	for (const operand of filterOperands(filter)) {
		if (operand === variable) return true;
	}

	return false;
}

/**
 * Replaces bare dynamic-variable sentinels (`$CURRENT_USER`, and in future `$NOW`)
 * with their resolved values wherever they appear as a filter operand. Callers pass only the
 * variables they can resolve, so an unresolvable sentinel is left in place.
 *
 * Lookup is own-key only: a bare `in` check reaches `Object.prototype`, which would rewrite an
 * operand that happens to name a prototype member (`'constructor'`, `'toString'`).
 */
export function substituteFilterVariables(filter: Filter, variables: Readonly<Record<string, string>>): Filter {
	return mapFilterOperands(filter, (operand) => (
		typeof operand === 'string' && Object.hasOwn(variables, operand) ? variables[operand] : operand
	)) as Filter;
}
